import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, X, Sparkles, MessageSquare } from 'lucide-react';
import { API } from '@/lib/api';

const sessionId = () => {
  let s = localStorage.getItem('ukgm_chat_session');
  if (!s) {
    s = 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('ukgm_chat_session', s);
  }
  return s;
};

export default function ChatWidget() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) setMessages([{ role: 'assistant', text: t('chat.welcome') }]);
  }, [open, t, messages.length]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const q = input.trim();
    if (!q || streaming) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text: q }, { role: 'assistant', text: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId(), message: q, language: i18n.language.split('-')[0] }),
      });
      if (!res.ok || !res.body) throw new Error('failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, '');
          if (line === '[DONE]') { setStreaming(false); return; }
          if (line.startsWith('[ERROR]')) {
            setMessages(m => { const c = [...m]; c[c.length-1] = { role:'assistant', text: 'Sorry, something went wrong. Please try again.' }; return c; });
            setStreaming(false); return;
          }
          const text = line.replace(/\\n/g, '\n');
          setMessages(m => { const c = [...m]; c[c.length-1] = { role:'assistant', text: (c[c.length-1].text || '') + text }; return c; });
        }
      }
    } catch {
      setMessages(m => { const c = [...m]; c[c.length-1] = { role:'assistant', text: 'Sorry, something went wrong.' }; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t('chat.open')}
          title={t('chat.open')}
          className="hidden md:inline-flex fixed bottom-4 left-4 z-40 items-center gap-2 bg-ukgm-blue text-white h-12 rounded-full shadow-xl hover:bg-ukgm-blueDark border border-ukgm-gold/40 pl-3 pr-4 md:pl-4 md:pr-5"
          data-testid="chat-open-btn"
        >
          <Sparkles className="w-4 h-4 text-ukgm-gold shrink-0" />
          <span className="text-sm font-medium">{t('chat.open')}</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:left-4 sm:right-auto sm:w-[380px] z-50 slide-up bg-white rounded-2xl shadow-2xl overflow-hidden border border-ukgm-gold/30" data-testid="chat-widget">
          <div className="bg-ukgm-blue px-4 py-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-ukgm-gold" />
              <div className="font-serif font-semibold">{t('chat.title')}</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="close" data-testid="chat-close-btn"><X className="w-5 h-5" /></button>
          </div>

          <div ref={scroller} className="h-80 overflow-y-auto px-4 py-4 space-y-3 bg-ukgm-cream" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-ukgm-blue text-white rounded-br-sm' : 'bg-white border border-ukgm-gold/25 text-ukgm-charcoal rounded-bl-sm'}`}>
                  {m.text || (streaming && i === messages.length - 1 ? '…' : '')}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 bg-white border-t border-ukgm-gold/20">
            <input value={input} onChange={e => setInput(e.target.value)} placeholder={t('chat.placeholder')}
              className="flex-1 px-3 py-2 rounded-full bg-ukgm-cream text-sm text-ukgm-charcoal placeholder:text-ukgm-slate/70"
              data-testid="chat-input" />
            <button type="submit" disabled={streaming || !input.trim()}
              className="w-9 h-9 rounded-full bg-ukgm-gold text-ukgm-blue flex items-center justify-center disabled:opacity-50 hover:bg-ukgm-goldDark"
              data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
